"use client";

import React, { forwardRef, useCallback, useRef } from "react";
import { CLOSE_GLYPH_PATH, CLOSE_GLYPH_VIEWBOX } from "../close-glyph";
import { sanitizeZipInput } from "../../services/zip/zip-validator";
import { HelperMessage } from "./helper-message";
import styles from "./text-field.module.css";
import { withSafeRender } from "../safe-render";

/**
 * TextField — a labelled, bordered field matching Eclipse "Text field"
 * (`dd2c88b84ecc9af38a77ba0be9f71b46db971ef1`), built for `molecules/zip-code`'s
 * ZIP input and reusable anywhere a short numeric-leaning value is typed.
 *
 * "use client": it filters what is typed as it is typed, and its clear button
 * returns focus through a ref. The full argument is in design-conflicts/zip-code.mdx.
 */
export interface TextFieldProps {
  /** Ties the label and the helper/error text to the input; must be unique on the page. */
  id: string;
  /** The visible label, inside the box: the 16px text while the field is empty and unfocused, a 12px line above the value otherwise. Also the accessible name. */
  label: string;
  /** The field's current value. Controlled — there is no uncontrolled mode. */
  value: string;
  /** Fires with the value AFTER the typing filter and the `maxLength` clamp have run. */
  onValueChange: (value: string) => void;
  /** Mobile keyboard hint. Defaults to `"numeric"` — this field was built for ZIP entry. */
  inputMode?: "numeric" | "text";
  /** Strips every non-digit character as it is typed. Defaults from `inputMode`: on for `"numeric"`, off otherwise. */
  digitsOnly?: boolean;
  /** A named typing filter, run on every change (typing and paste alike) before `maxLength`. `"zip"` is the legacy filter: the first run of up to five digits, ended by a dot. Overrides `digitsOnly`. */
  inputFilter?: "zip";
  /** The autofill hint (WCAG 1.3.5), e.g. `"postal-code"`. Unset, none is sent. */
  autoComplete?: string;
  /** Caps the value length client-side, same as the native attribute. */
  maxLength?: number;
  /** Shows the error treatment and switches the helper row to `errorMessage`. */
  error?: boolean;
  /** Shown instead of `helperMessage` while `error` is true. */
  errorMessage?: string;
  /** Shown under the field while there is no error. */
  helperMessage?: string;
  disabled?: boolean;
  /** Shows a clear button while the field has a value. A real button, so the keyboard reaches it; it fires this, then returns focus to the input. Carries no data — the host empties its own value. */
  onClear?: () => void;
  /** The clear button's accessible name. Defaults to "Click to clear {label} input." */
  clearLabel?: string;
  /** Focuses the input on mount — rare; prefer the forwarded ref for a focus that fires later. */
  autoFocus?: boolean;
  className?: string;
}

/**
 * The ref is forwarded to the `<input>` so a host that owns a disclosure
 * (`molecules/zip-code`) can focus the field the moment its own state says the field
 * became visible — `autoFocus` fires only at mount.
 */
const TextFieldImpl = forwardRef<HTMLInputElement, TextFieldProps>(({
  id,
  label,
  value,
  onValueChange,
  inputMode = "numeric",
  digitsOnly = inputMode === "numeric",
  inputFilter,
  autoComplete,
  maxLength,
  error = false,
  errorMessage,
  helperMessage,
  disabled = false,
  onClear,
  clearLabel,
  autoFocus = false,
  className
}, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setRefs = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);
  const messageId = `${id}-message`;
  const message = error ? errorMessage : helperMessage;
  const hasMessage = !(message === undefined || message === null || message === "");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let next = event.target.value;
    if (inputFilter === "zip") {
      next = sanitizeZipInput(next);
    } else if (digitsOnly) {
      next = next.replace(/\D/g, "");
    }
    if (typeof maxLength === "number") {
      next = next.slice(0, maxLength);
    }
    onValueChange(next);
  };

  return (
    <div className={[styles.root, className].filter(Boolean).join(" ")}>
      <div className={styles.inputWrap}>
        <input
          aria-describedby={hasMessage ? messageId : undefined}
          aria-invalid={error || undefined}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={[
            styles.input,
            error ? styles.inputError : null,
            onClear ? styles.inputClearable : null
          ].filter(Boolean).join(" ")}
          disabled={disabled}
          id={id}
          inputMode={inputMode}
          maxLength={maxLength}
          onChange={handleChange}
          placeholder=" "
          ref={setRefs}
          type="text"
          value={value}
        />
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {onClear && value !== "" && !disabled && (
          <button
            aria-label={clearLabel || `Click to clear ${label} input.`}
            className={styles.clear}
            onClick={() => {
              onClear();
              inputRef.current?.focus();
            }}
            type="button"
          >
            <svg aria-hidden="true" className={styles.clearIcon} focusable="false" viewBox={CLOSE_GLYPH_VIEWBOX}>
              <path d={CLOSE_GLYPH_PATH} fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
      {hasMessage && (
        <HelperMessage id={messageId} tone={error ? "error" : disabled ? "disabled" : "helper"}>
          {message}
        </HelperMessage>
      )}
    </div>
  );
});
export const TextField = withSafeRender(TextFieldImpl, "TextField");

// forwardRef's wrapper infers no name from an arrow function; without this, DevTools reads "ForwardRef".
TextField.displayName = "TextField";

export default TextField;
